import * as React from 'react';
import { Box, ThemeProvider, Typography } from '@mui/material';
import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { generateTheme } from '../themes';

const PADDING_MAP = {
    'None':   { xs: '0px',  md: '0px' },
    'Small':  { xs: '24px', md: '48px' },
    'Normal': { xs: '48px', md: '80px' },
    'Large':  { xs: '72px', md: '120px' }
};

const DEFAULT_PAD = { xs: '48px', md: '80px' };

const resolvePadding = (value, bp) => {
    if (!value || value === 'Auto') return DEFAULT_PAD[bp];
    return PADDING_MAP[value]?.[bp] ?? DEFAULT_PAD[bp];
};

const TITLE_SIZES = { xs: '1.75rem', sm: '2.25rem', md: '3.5rem', lg: '4rem' };

const Cell = ({ value, featured }) => {
    if (value === true) {
        return (
            <Box
                component="span"
                aria-label="Yes"
                sx={{
                    color: '#0091A5',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    lineHeight: 1
                }}
            >
                ✓
            </Box>
        );
    }
    if (value === false || value === null || value === undefined) return null;

    let text;
    let tone;
    if (typeof value === 'string') {
        text = value;
    } else if (typeof value === 'object') {
        text = value.text || '';
        tone = value.tone;
    } else {
        return null;
    }

    const toneSx =
        tone === 'muted'
            ? { fontStyle: 'italic', color: featured ? 'rgba(255,255,255,0.75)' : 'text.secondary' }
            : tone === 'negative'
                ? { color: '#B8326C', fontWeight: 500 }
                : tone === 'positive'
                    ? { color: '#0091A5', fontWeight: 600 }
                    : { color: featured ? 'text.primary' : 'text.primary', fontWeight: 500 };

    return (
        <Box
            component="span"
            sx={{ fontSize: { xs: '0.9rem', md: '0.95rem' }, lineHeight: 1.4, ...toneSx }}
            dangerouslySetInnerHTML={{ __html: text }}
        />
    );
};

const ComparisonSlice = ({ data }) => {
    const {
        colorTheme = 'off-white',
        eyebrow,
        title,
        body,
        footnote,
        comparison = {},
        gradientHeadline = false,
        sliceMarginTop = 'Auto',
        sliceMarginTopMobile = 'Auto',
        sliceMarginBottom = 'Auto',
        sliceMarginBottomMobile = 'Auto'
    } = data;

    const theme = generateTheme(colorTheme);
    const containerRef = useRef(null);

    const { featureLabel = 'FUNKTION', columns = [], rows = [] } = comparison || {};

    const slicePt = { xs: resolvePadding(sliceMarginTopMobile, 'xs'), md: resolvePadding(sliceMarginTop, 'md') };
    const slicePb = { xs: resolvePadding(sliceMarginBottomMobile, 'xs'), md: resolvePadding(sliceMarginBottom, 'md') };

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const targets = gsap.utils.toArray('.cmp-animate', containerRef.current);
        targets.forEach((el, i) => {
            gsap.fromTo(
                el,
                { opacity: 0, y: 10 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    delay: 0.04 * i,
                    ease: 'easeIn',
                    scrollTrigger: {
                        id: 'cmpAnimate',
                        trigger: el,
                        start: 'top 88%',
                        markers: false
                    }
                }
            );
        });
    }, []);

    const FEATURED_BG = '#191126';
    const FEATURED_CELL_BG = '#EEEBF4';
    const ROW_BORDER = '#e7e3d9';
    const TABLE_BORDER = '#dedacf';
    const CELL_PAD_Y = { xs: '16px', md: '22px' };
    const CELL_PAD_X = { xs: '12px', md: '20px' };

    return (
        <ThemeProvider theme={theme}>
            <Box
                ref={containerRef}
                sx={{
                    background: (t) => t.palette.background.default,
                    px: { xs: 3, md: 12 },
                    pt: slicePt,
                    pb: slicePb
                }}
            >
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {eyebrow && (
                        <Typography
                            className="cmp-animate"
                            component="span"
                            sx={{
                                display: 'block',
                                color: 'text.secondary',
                                letterSpacing: '0.2em',
                                textTransform: 'uppercase',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                opacity: 0.7,
                                mb: { xs: 2, md: 2.5 }
                            }}
                            dangerouslySetInnerHTML={{ __html: eyebrow }}
                        />
                    )}

                    {title && (
                        <Typography
                            className="cmp-animate"
                            component="h2"
                            lang="sv"
                            sx={{
                                color: 'text.primary',
                                fontFamily: 'Kanit, sans-serif',
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                fontSize: TITLE_SIZES,
                                lineHeight: 1.05,
                                letterSpacing: '-0.01em',
                                overflowWrap: 'break-word',
                                hyphens: 'auto',
                                maxWidth: 820,
                                mb: body ? 3 : 5,
                                ...(gradientHeadline ? {
                                    background: 'linear-gradient(90deg, rgb(6, 194, 220) 0%, rgb(0, 158, 187) 50%, rgb(92, 51, 215) 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    color: 'transparent'
                                } : {})
                            }}
                            dangerouslySetInnerHTML={{ __html: title }}
                        />
                    )}

                    {body && (
                        <Typography
                            className="cmp-animate"
                            component="p"
                            sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '1rem', md: '1.125rem' },
                                lineHeight: 1.6,
                                maxWidth: 640,
                                mb: { xs: 5, md: 7 }
                            }}
                            dangerouslySetInnerHTML={{ __html: body }}
                        />
                    )}

                    {columns.length > 0 && (
                        <Box
                            className="cmp-animate"
                            sx={{
                                border: `1px solid ${TABLE_BORDER}`,
                                borderRadius: '18px',
                                overflow: 'hidden',
                                background: '#fff',
                                display: { xs: 'none', md: 'block' }
                            }}
                        >
                            <Box sx={{ overflowX: 'auto' }}>
                                <Box
                                    component="table"
                                    sx={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        tableLayout: 'fixed',
                                        minWidth: columns.length >= 3 ? 680 : 520
                                    }}
                                >
                                    <Box component="thead">
                                        <Box component="tr">
                                            <Box
                                                component="th"
                                                sx={{
                                                    textAlign: 'left',
                                                    padding: `${CELL_PAD_Y.md} ${CELL_PAD_X.md}`,
                                                    verticalAlign: 'bottom',
                                                    fontSize: '0.75rem',
                                                    letterSpacing: '0.18em',
                                                    textTransform: 'uppercase',
                                                    fontWeight: 600,
                                                    color: 'text.secondary',
                                                    width: { xs: '34%', md: '28%' }
                                                }}
                                                dangerouslySetInnerHTML={{ __html: featureLabel }}
                                            />
                                            {columns.map((col, i) => (
                                                <Box
                                                    key={i}
                                                    component="th"
                                                    sx={{
                                                        position: 'relative',
                                                        textAlign: 'center',
                                                        padding: { xs: '18px 10px', md: '28px 16px 24px' },
                                                        background: col.featured ? FEATURED_BG : 'transparent',
                                                        color: col.featured ? '#fff' : 'text.primary',
                                                        verticalAlign: 'bottom',
                                                        ...(col.featured && {
                                                            '&::before': {
                                                                content: '""',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                height: '4px',
                                                                background: 'linear-gradient(90deg, rgb(6, 194, 220) 0%, rgb(0, 158, 187) 50%, rgb(92, 51, 215) 100%)'
                                                            }
                                                        })
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: 'block',
                                                            fontFamily: 'Kanit, sans-serif',
                                                            fontWeight: 800,
                                                            fontSize: { xs: '1.25rem', md: '1.6rem' },
                                                            lineHeight: 1.1
                                                        }}
                                                    >
                                                        {col.name}
                                                    </Box>
                                                    {col.price && (
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                display: 'block',
                                                                mt: 1.25,
                                                                fontWeight: 600,
                                                                fontSize: { xs: '0.9rem', md: '1rem' }
                                                            }}
                                                        >
                                                            {col.price}
                                                        </Box>
                                                    )}
                                                    {col.subtitle && (
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                display: 'block',
                                                                fontSize: { xs: '0.7rem', md: '0.8rem' },
                                                                fontWeight: 400,
                                                                opacity: col.featured ? 0.85 : 0.6,
                                                                mt: 0.25,
                                                                ...(col.featured && { color: '#c6b7ff' })
                                                            }}
                                                        >
                                                            {col.subtitle}
                                                        </Box>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                    <Box component="tbody">
                                        {rows.map((row, ri) => (
                                            <Box
                                                component="tr"
                                                key={ri}
                                                sx={{
                                                    borderTop: `1px solid ${ROW_BORDER}`
                                                }}
                                            >
                                                <Box
                                                    component="td"
                                                    sx={{
                                                        padding: { xs: `${CELL_PAD_Y.xs} ${CELL_PAD_X.xs}`, md: `${CELL_PAD_Y.md} ${CELL_PAD_X.md}` },
                                                        fontWeight: 600,
                                                        color: 'text.primary',
                                                        fontSize: { xs: '0.9rem', md: '1rem' }
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: row.label }}
                                                />
                                                {columns.map((col, ci) => (
                                                    <Box
                                                        key={ci}
                                                        component="td"
                                                        sx={{
                                                            textAlign: 'center',
                                                            padding: { xs: `${CELL_PAD_Y.xs} ${CELL_PAD_X.xs}`, md: `${CELL_PAD_Y.md} ${CELL_PAD_X.md}` },
                                                            background: col.featured ? FEATURED_CELL_BG : 'transparent'
                                                        }}
                                                    >
                                                        <Cell value={row.values && row.values[ci]} featured={col.featured} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {columns.length > 0 && (
                        <Box
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                flexDirection: 'column',
                                gap: 2.5
                            }}
                        >
                            {columns.map((col, ci) => (
                                <Box
                                    key={ci}
                                    className="cmp-animate"
                                    sx={{
                                        position: 'relative',
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        border: col.featured ? 'none' : `1px solid ${TABLE_BORDER}`,
                                        background: col.featured ? FEATURED_BG : '#fff',
                                        color: col.featured ? '#fff' : 'text.primary',
                                        ...(col.featured && {
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: '4px',
                                                background: 'linear-gradient(90deg, rgb(6, 194, 220) 0%, rgb(0, 158, 187) 50%, rgb(92, 51, 215) 100%)',
                                                zIndex: 1
                                            }
                                        })
                                    }}
                                >
                                    <Box sx={{ px: 2.5, pt: col.featured ? 3 : 2.5, pb: 2 }}>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'block',
                                                fontFamily: 'Kanit, sans-serif',
                                                fontWeight: 800,
                                                fontSize: '1.5rem',
                                                lineHeight: 1.1
                                            }}
                                        >
                                            {col.name}
                                        </Box>
                                        {col.price && (
                                            <Box component="span" sx={{ display: 'block', mt: 0.75, fontWeight: 600, fontSize: '0.95rem' }}>
                                                {col.price}
                                            </Box>
                                        )}
                                        {col.subtitle && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'block',
                                                    fontSize: '0.75rem',
                                                    opacity: col.featured ? 0.85 : 0.6,
                                                    mt: 0.25,
                                                    ...(col.featured && { color: '#c6b7ff' })
                                                }}
                                            >
                                                {col.subtitle}
                                            </Box>
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            background: col.featured ? FEATURED_CELL_BG : '#fff',
                                            color: 'text.primary'
                                        }}
                                    >
                                        {rows.map((row, ri) => (
                                            <Box
                                                key={ri}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 2,
                                                    px: 2.5,
                                                    py: 1.5,
                                                    borderTop: `1px solid ${ROW_BORDER}`
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontSize: '0.9rem',
                                                        fontWeight: 500,
                                                        color: 'text.primary',
                                                        flex: '1 1 auto',
                                                        minWidth: 0
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: row.label }}
                                                />
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        textAlign: 'right',
                                                        flex: '0 0 auto',
                                                        maxWidth: '55%'
                                                    }}
                                                >
                                                    <Cell value={row.values && row.values[ci]} featured={col.featured} />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {footnote && (
                        <Typography
                            component="p"
                            sx={{
                                mt: { xs: 3, md: 4 },
                                color: 'text.secondary',
                                fontSize: { xs: '0.8rem', md: '0.85rem' },
                                lineHeight: 1.6,
                                maxWidth: 820
                            }}
                            dangerouslySetInnerHTML={{ __html: footnote }}
                        />
                    )}
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default ComparisonSlice;
